import { driver, DriveStep } from 'driver.js'
import i18n from 'i18next'
import { Classes } from 'jss'

import endUserManual from '../../assets/ai-go-studio-end-user-manual.pdf'
import { Settings } from '../../components/Settings/types'

const TITLE = i18n.t('Products')

const getDriver = (
  classesDriver: Classes<'driver'>,
  setProductsCreateUpdateModal: React.Dispatch<React.SetStateAction<boolean>>,
  settings: Settings,
  updateSettings: (settings: Partial<Settings>) => void,
  updatePreference: () => void,
  productsCreateUpdateModal: boolean
) => {
  const driverSteps: DriveStep[] = [
    {
      popover: {
        title: i18n.t('Welcome to AI-go Studio!'),
        description: `
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">          
          ${i18n.t(
            'This guided tour will help you understand the main functionalities of AI-go Studio.'
          )}
          <br/><br/>
          ${i18n.t('You can always access the guided tour by clicking on the')}
          <b><i style="font-size:15px" class="fa">&#xf059;</i></b>
          ${i18n.t('icon in the upper right corner.')}
        `,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#newProductButtonId',
      popover: {
        title: TITLE,
        description: `
          ${i18n.t('Click')} 
            <b>${i18n.t('New product')}</b> 
          ${i18n.t('to create a new {{ item }}.', {
            item: i18n.t('product'),
          })}<br/>        
          ${i18n.t(
            'You can create as many products as you want, and each product contains a set of images, trainings and tests.'
          )}`,
        side: 'bottom',
        align: 'start',
        onNextClick: () => {
          setProductsCreateUpdateModal(true)
        },
      },
    },
    {
      element: '#productNameId',
      popover: {
        title: TITLE,
        description: i18n.t('Enter the name of the new {{ item }}.', {
          item: i18n.t('product'),
        }),
        side: 'right',
        align: 'start',
        onPrevClick: () => {
          setProductsCreateUpdateModal(false)
          driverObj.movePrevious()
        },
      },
    },
    {
      element: '#productDescriptionId',
      popover: {
        title: TITLE,
        description: i18n.t('Enter the description of the new {{ item }}.', {
          item: i18n.t('product'),
        }),
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '#productTypeAndClassesId',
      popover: {
        title: TITLE,
        description: `${i18n.t('Select the type of the new product.')}<br/>
        <ul style="padding-left:15px;">
          <li><b>${i18n.t('Classification')}</b>: ${i18n.t(
          'to classify images in their entirety.'
        )}</li>
          <li><b>${i18n.t('Segmentation')}</b>: ${i18n.t(
          'to locate and identify distinct regions of interest within images.'
        )}</li>
          <li><b>${i18n.t('Patch')}</b>: ${i18n.t(
          'expands upon basic classification by evaluating multiple portions of images.'
        )}</li>
          <li><b>Anomaly detection</b>: ${i18n.t(
            'to detect deviations or anomalies within images.'
          )}</li>
        </ul>
        ${i18n.t(
          'Add the classes list, based on the chosen training type. Optionally, a pre-configured list of classes can be selected, avoiding to input them manually.'
        )}`,
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '#productButtonsFooterId',
      popover: {
        title: TITLE,
        description: `${i18n.t('Click')} <b>${i18n.t('Create')}</b> 
        ${i18n.t('to add the new {{ item }} on top of the {{ items }} list.', {
          item: i18n.t('product'),
          items: i18n.t('products'),
        })}`,
        side: 'right',
        align: 'start',
        onNextClick: () => {
          setProductsCreateUpdateModal(false)
          driverObj.moveNext()
        },
      },
    },
    {
      element: '#divProductsListId',
      popover: {
        title: TITLE,
        description: `
          ${i18n.t('Here you can see all your {{ items }}.', {
            items: i18n.t('products'),
          })}
          ${i18n.t('Click on a {{ item }} to see its details.', {
            item: i18n.t('product'),
          })}<br/><br/>
          ${i18n.t(
            'For each product are displayed its name, type, classes, date and time of creation, and the counts of trainings and tests associated with it (based on their execution status).'
          )}<br/><br/>
          ${i18n.t(
            'It is possible to edit or delete a product. Additionally, you can export and import the annotations and the images of a product.'
          )}<br/><br/>
          ${i18n.t('Additional information can be found in the')}
            <a target='_blank' href='${endUserManual}'>
              ${i18n.t('End user manual (PDF)')}
            </a>
          <br/><br/>
          <label htmlFor="doNotShowAgainCheckbox">
            <input
              type="checkbox"
              id="doNotShowAgainCheckbox"  
              ${
                settings.preference__show_guided_tour ? '' : 'checked'
              }            
            />
            ${i18n.t('Do not show again')}
          </label>`,
        side: 'bottom',
        align: 'start',
        onPopoverRender: () => {
          document
            .getElementById('doNotShowAgainCheckbox')
            ?.addEventListener('change', () => {
              updateSettings({
                preference__show_guided_tour: !settings.preference__show_guided_tour,
              })
            })
        },
        onPrevClick: () => {
          setProductsCreateUpdateModal(true)
        },
      },
    },
  ]

  const driverObj = driver({
    showProgress: true,
    steps: driverSteps,
    nextBtnText: i18n.t('Next'),
    prevBtnText: i18n.t('Previous'),
    doneBtnText: i18n.t('Done'),
    popoverClass: classesDriver.driver,
    onPopoverRender: (popover) => {
      const skipButton = document.createElement('button')
      skipButton.id = 'skipButton'
      skipButton.innerText = i18n.t('Skip all')
      popover.footerButtons.appendChild(skipButton)
      skipButton.addEventListener('click', () => {
        const stepsLength = driverObj.getConfig('steps')?.length
        if (stepsLength) {
          driverObj.drive(stepsLength - 1)
        }
        if (productsCreateUpdateModal) setProductsCreateUpdateModal(false)
      })
    },
    onDestroyStarted: () => {
      if (driverObj.isLastStep()) {
        updatePreference()
      }
      if (productsCreateUpdateModal) setProductsCreateUpdateModal(false)
      driverObj.destroy()
    },
  })

  return driverObj
}

export default getDriver
